/**
 * frame-validator.ts
 *
 * High-performance frame quality and integrity validation.
 * Detects whether a canvas or image is an unrendered WebGL clear buffer,
 * solid vertical linear gradient, pure solid color, or corrupt/blank frame.
 */

let sharedProbeCanvas: HTMLCanvasElement | null = null;
let sharedProbeCtx: CanvasRenderingContext2D | null = null;

/**
 * Checks if a canvas or image is a corrupt frame, unrendered WebGL clear buffer,
 * or pure vertical linear gradient.
 *
 * In KrPano HTML5 viewer, when WebGL canvas is cleared or unrendered, KrPano renders
 * a pure vertical linear gradient where every pixel along any horizontal line X is identical.
 * Real 360 panorama scenes exhibit high horizontal variance across rows (typically > 1000).
 *
 * @param source HTMLCanvasElement or HTMLImageElement to test
 * @param rowVarianceThreshold Threshold for maximum horizontal variance across rows (default: 50)
 * @param totalVarianceThreshold Threshold for total image variance (default: 10)
 * @returns true if frame is corrupt, gradient, blank, or null; false if it contains real visual content
 */
export function isCorruptOrGradientCanvas(
    source: HTMLCanvasElement | HTMLImageElement | null | undefined,
    rowVarianceThreshold = 50,
    totalVarianceThreshold = 10
): boolean {
    if (!source) return true;
    const w = 'naturalWidth' in source ? (source as HTMLImageElement).naturalWidth : source.width;
    const h = 'naturalHeight' in source ? (source as HTMLImageElement).naturalHeight : source.height;
    if (!w || !h || w <= 0 || h <= 0) return true;

    if (typeof document === 'undefined') return false;

    try {
        if (!sharedProbeCanvas) {
            sharedProbeCanvas = document.createElement('canvas');
            sharedProbeCanvas.width = 16;
            sharedProbeCanvas.height = 16;
            sharedProbeCtx = sharedProbeCanvas.getContext('2d', { willReadFrequently: true });
        }
        if (!sharedProbeCtx) return false;

        // Draw source scaled down to 16x16
        sharedProbeCtx.clearRect(0, 0, 16, 16);
        sharedProbeCtx.drawImage(source, 0, 0, 16, 16);

        const imgData = sharedProbeCtx.getImageData(0, 0, 16, 16);
        const data = imgData.data;

        let maxRowVariance = 0;
        let totalSumR = 0;
        let totalSumG = 0;
        let totalSumB = 0;

        for (let y = 0; y < 16; y++) {
            let rowSumR = 0;
            let rowSumG = 0;
            let rowSumB = 0;
            const rowOffset = y * 16 * 4;

            for (let x = 0; x < 16; x++) {
                const idx = rowOffset + x * 4;
                const r = data[idx];
                const g = data[idx + 1];
                const b = data[idx + 2];
                rowSumR += r;
                rowSumG += g;
                rowSumB += b;
            }

            const meanR = rowSumR / 16;
            const meanG = rowSumG / 16;
            const meanB = rowSumB / 16;

            let rowVarR = 0;
            let rowVarG = 0;
            let rowVarB = 0;

            for (let x = 0; x < 16; x++) {
                const idx = rowOffset + x * 4;
                const dr = data[idx] - meanR;
                const dg = data[idx + 1] - meanG;
                const db = data[idx + 2] - meanB;
                rowVarR += dr * dr;
                rowVarG += dg * dg;
                rowVarB += db * db;
            }

            const rowVar = (rowVarR + rowVarG + rowVarB) / 48; // (16 * 3)
            if (rowVar > maxRowVariance) {
                maxRowVariance = rowVar;
            }

            totalSumR += rowSumR;
            totalSumG += rowSumG;
            totalSumB += rowSumB;
        }

        // In pure vertical gradients (or blank unrendered WebGL clear buffers),
        // all pixels across any horizontal row are identical (maxRowVariance < 15, empirical test)
        // In real 360 panorama scenes, maxRowVariance is consistently > 1600.
        if (maxRowVariance < rowVarianceThreshold) {
            return true;
        }

        // Check total color variance across entire 16x16 frame
        const meanAllR = totalSumR / 256;
        const meanAllG = totalSumG / 256;
        const meanAllB = totalSumB / 256;

        let totalVarR = 0;
        let totalVarG = 0;
        let totalVarB = 0;

        for (let i = 0; i < 256; i++) {
            const idx = i * 4;
            const dr = data[idx] - meanAllR;
            const dg = data[idx + 1] - meanAllG;
            const db = data[idx + 2] - meanAllB;
            totalVarR += dr * dr;
            totalVarG += dg * dg;
            totalVarB += db * db;
        }

        const totalVar = (totalVarR + totalVarG + totalVarB) / 768; // (256 * 3)
        if (totalVar < totalVarianceThreshold) {
            return true;
        }

        return false;
    } catch {
        // If security exception (cross-origin tainted canvas), don't treat as corrupt
        return false;
    }
}
